import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, } from "react";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "../../provider";
import { AuthLayout } from "./AuthLayout";
export function LoginMFAChallenge() {
    const navigate = useNavigate();
    const { login, sessionToken, email, authClient } = useAuth();
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const inputRefs = useRef([]);
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);
    const handleChange = (index, value) => {
        if (value && !/^\d$/.test(value))
            return;
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        const allFilled = newCode.every((d) => d !== "");
        if (allFilled) {
            submitCode(newCode.join(""));
        }
    };
    const submitCode = async (fullCode) => {
        if (!sessionToken) {
            setError("Session expired. Please sign in again.");
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const res = await authClient.verifyMFA(fullCode, sessionToken, email ?? "");
            const result = res.data;
            // Token is in HttpOnly cookie — just record expires_in and mark authenticated.
            login(result.expires_in);
            navigate("/dashboard");
        }
        catch {
            setCode(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
            setError("Incorrect code. Please try again.");
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);
        const digits = pastedData.split("").filter((char) => /^\d$/.test(char));
        const newCode = [...code];
        digits.forEach((digit, i) => {
            if (i < 6) {
                newCode[i] = digit;
            }
        });
        setCode(newCode);
        const nextIndex = Math.min(digits.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };
    return (_jsxs(AuthLayout, { children: [_jsx("button", { onClick: () => navigate("/auth/password"), className: "mb-6 p-2 -ml-2 text-duvi-text-secondary hover:text-duvi-text-primary hover:bg-duvi-elevated rounded transition-colors", title: "Back", children: _jsx(ChevronLeft, { className: "w-5 h-5" }) }), _jsx("h1", { className: "text-duvi-text-primary mb-2", style: {
                    fontSize: "var(--text-xl)",
                    fontWeight: "var(--font-weight-semibold)",
                    lineHeight: "var(--line-height-heading)",
                }, children: "Two-factor authentication" }), _jsx("p", { className: "text-duvi-text-secondary mb-8", style: {
                    fontSize: "var(--text-sm)",
                    lineHeight: "var(--line-height-body)",
                }, children: "Enter the 6-digit code from your authenticator app." }), _jsx("div", { className: "flex gap-2 mb-3", onPaste: handlePaste, children: code.map((digit, index) => (_jsx("input", { ref: (el) => (inputRefs.current[index] = el), type: "text", inputMode: "numeric", maxLength: 1, "data-testid": "mfa-input", value: digit, onChange: (e) => handleChange(index, e.target.value), onKeyDown: (e) => handleKeyDown(index, e), className: "w-full aspect-square bg-duvi-bg border border-duvi-border rounded-[6px] text-center text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors", style: {
                        fontSize: "var(--text-2xl)",
                        fontWeight: "var(--font-weight-bold)",
                        fontFamily: "monospace",
                    } }, index))) }), error && (_jsx("div", { className: "mb-4 text-duvi-destructive text-center", "data-testid": "mfa-error", style: { fontSize: "var(--text-sm)" }, children: error })), isLoading && (_jsx("div", { className: "mb-4 text-duvi-text-secondary text-center", style: { fontSize: "var(--text-sm)" }, children: "Verifying..." }))] }));
}
//# sourceMappingURL=LoginMFAChallenge.js.map